import Drawer from "@mui/material/Drawer";
import { Button, Icon, LoadingSpinner } from "pepsico-ds";
import PropTypes from "prop-types";
import { useContext, useMemo } from "react";
import { GlobalConfigContext } from "../../../context/GlobalConfigContext";
import ToastNotification from "../../common/toastNotification/ToastNotification";
import ConfigurationModals from "../PartnerGenericConfigurationPages/ConfigurationModals";
import DrawerFieldRenderer from "./DrawerFieldRenderer";
import JsonFormsDrawerContent from "./JsonFormsDrawerContent";
import {
  configFieldSchemas,
  generateFieldsFromData,
  getFieldValidators,
  jsonFormsConfigs,
  staticConfigs,
} from "./configFieldSchemas";
import "./configurationDrawer.scss";
import useDrawerConfigForm from "./hooks/useDrawerConfigForm";
import useLoadSchema from "./hooks/useLoadSchema";

/**
 * Unified configuration drawer component.
 * Attempts to load schemas from the API first (via loadUiComponent).
 * If the API returns schemas, renders JsonForms. Otherwise falls back
 * to field-based, inline JsonForms, or dynamic rendering.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the drawer is open
 * @param {Function} props.onClose - Callback to close the drawer
 * @param {string} props.configKey - Configuration key (e.g., 'generic_configuration')
 * @param {string} props.mode - "edit" or "view"
 */
const COMPACT_DRAWER_CONFIGS = new Set([
  "social_media_links",
  "widgets_configuration",
]);

const ConfigurationDrawer = ({
  open,
  onClose,
  onEdit,
  canEdit = false,
  configKey,
  mode,
}) => {
  if (!configKey) return null;

  const isCompactDrawer = COMPACT_DRAWER_CONFIGS.has(configKey);

  return (
    <Drawer
      open={open}
      onClose={() => {}} // Handled by internal close logic
      anchor="right"
      classes={{
        paper: `config-drawer${isCompactDrawer ? " config-drawer--compact" : ""}`,
      }}
    >
      <ConfigDrawerContent
        configKey={configKey}
        mode={mode}
        isOpen={open}
        onClose={onClose}
        onEdit={onEdit}
        canEdit={canEdit}
      />
    </Drawer>
  );
};

ConfigurationDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  canEdit: PropTypes.bool,
  configKey: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["edit", "view"]).isRequired,
};

export default ConfigurationDrawer;

// ─── API-First Content Router ───────────────────────────────────────────────

/**
 * Tries to load schemas from the API first.
 * If schemas are returned → renders JsonForms with preloaded schemas.
 * If API fails → falls back to current rendering logic.
 */
const ConfigDrawerContent = ({
  configKey,
  mode,
  isOpen,
  onClose,
  onEdit,
  canEdit,
}) => {
  const { jsonSchema, uiSchema, loading, failed } = useLoadSchema(configKey, isOpen);

  const hasApiSchemas = !loading && !failed && jsonSchema && uiSchema;

  if (loading) {
    return (
      <>
        <div className="config-drawer-loading">
          <LoadingSpinner size={{ height: "2rem", width: "2rem" }} />
        </div>
        <div className="config-drawer-actions">
          <Button size="medium" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </>
    );
  }

  const staticConfig = staticConfigs[configKey];
  const dataConfigKey = staticConfig?.dataKey || configKey;

  if (hasApiSchemas) {
    return (
      <ApiSchemaConfigDrawer
        configKey={configKey}
        dataConfigKey={dataConfigKey}
        mode={mode}
        isOpen={isOpen}
        onClose={onClose}
        onEdit={onEdit}
        canEdit={canEdit}
        preloadedSchemas={{ jsonSchema, uiSchema }}
      />
    );
  }

  // Static configs with noFallback: show empty state when API fails
  if (staticConfig?.noFallback) {
    return (
      <>
        <div className="config-drawer-header">
          <p className="config-drawer-title">{staticConfig.title}</p>
          {staticConfig.description && (
            <p className="config-drawer-description">{staticConfig.description}</p>
          )}
        </div>
        <div className="config-drawer-error">
          <p>Failed to load configuration settings. Please try again later.</p>
        </div>
        <div className="config-drawer-actions">
          <Button size="medium" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </>
    );
  }

  // Fallback: API failed or returned no schemas
  const isJsonFormsConfig = configKey in jsonFormsConfigs;
  const isFieldBasedConfig = configKey in configFieldSchemas;

  if (isJsonFormsConfig) {
    return (
      <JsonFormsConfigDrawer
        configKey={configKey}
        mode={mode}
        isOpen={isOpen}
        onClose={onClose}
        onEdit={onEdit}
        canEdit={canEdit}
      />
    );
  }

  if (isFieldBasedConfig) {
    return (
      <FieldBasedConfigDrawer
        configKey={configKey}
        mode={mode}
        isOpen={isOpen}
        onClose={onClose}
      />
    );
  }

  return (
    <DynamicFieldConfigDrawer
      configKey={configKey}
      mode={mode}
      isOpen={isOpen}
      onClose={onClose}
    />
  );
};

ConfigDrawerContent.propTypes = {
  configKey: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["edit", "view"]).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  canEdit: PropTypes.bool,
};

const DrawerHeader = ({ title, description, onClose, showClose }) => (
  <div className="config-drawer-header">
    <div className="config-drawer-header-row">
      <p className="config-drawer-title">{title}</p>
      {showClose && (
        <button
          aria-label="Close configuration"
          className="config-drawer-close"
          onClick={onClose}
          type="button"
        >
          <Icon icon="close" size="small" />
        </button>
      )}
    </div>
    {description && <p className="config-drawer-description">{description}</p>}
  </div>
);

DrawerHeader.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  showClose: PropTypes.bool,
};

// ─── API Schema Drawer (preloaded schemas from loadUiComponent) ─────────────

const ApiSchemaConfigDrawer = ({
  configKey,
  dataConfigKey,
  mode,
  isOpen,
  onClose,
  onEdit,
  canEdit,
  preloadedSchemas,
}) => {
  const { programConfigurations } = useContext(GlobalConfigContext);
  const staticConfig = staticConfigs[configKey];

  const configEntry = useMemo(
    () => programConfigurations?.find((c) => c.key === configKey),
    [programConfigurations, configKey]
  );

  const title =
    staticConfig?.title ||
    configEntry?.value?.title ||
    configKey.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const description =
    staticConfig?.description || configEntry?.value?.description || "";

  return (
    <>
      <DrawerHeader
        description={description}
        onClose={onClose}
        showClose={mode === "view"}
        title={title}
      />

      <JsonFormsDrawerContent
        configKey={dataConfigKey || configKey}
        mode={mode}
        isOpen={isOpen}
        onClose={onClose}
        onEdit={onEdit}
        canEdit={canEdit}
        preloadedSchemas={preloadedSchemas}
      />
    </>
  );
};

ApiSchemaConfigDrawer.propTypes = {
  configKey: PropTypes.string.isRequired,
  dataConfigKey: PropTypes.string,
  mode: PropTypes.oneOf(["edit", "view"]).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  canEdit: PropTypes.bool,
  preloadedSchemas: PropTypes.shape({
    jsonSchema: PropTypes.object,
    uiSchema: PropTypes.object,
  }).isRequired,
};

// ─── Field-Based Drawer ─────────────────────────────────────────────────────

const FieldBasedConfigDrawer = ({ configKey, mode, isOpen, onClose }) => {
  const schema = configFieldSchemas[configKey];
  const validators = getFieldValidators(configKey);
  const isEdit = mode === "edit";

  const {
    formData,
    fieldErrors,
    isUpdated,
    isLoading,
    hasFieldErrors,
    confirmationModalOpen,
    discardModalOpen,
    toast,
    setToast,
    setConfirmationModalOpen,
    setDiscardModalOpen,
    handleFieldChange,
    handleToggleChange,
    handleSaveConfirmation,
    handleDiscard,
    getDeepValue,
  } = useDrawerConfigForm(configKey, validators, isOpen);

  const handleClose = () => {
    if (isEdit && isUpdated) {
      setDiscardModalOpen(true);
    } else {
      onClose();
    }
  };

  const handleDiscardAndClose = () => {
    handleDiscard();
    onClose();
  };

  return (
    <>
      <div className="config-drawer-header">
        <p className="config-drawer-title">{schema.title}</p>
        {schema.description && (
          <p className="config-drawer-description">{schema.description}</p>
        )}
      </div>

      <div className="config-drawer-content">
        {configKey === "generic_configuration" && (
          <div className="config-drawer-program-id">
            <span className="config-drawer-program-id-label">
              Program Identification
            </span>
            <span className="config-drawer-program-id-value">
              {localStorage.getItem("programId") || "-"}
            </span>
          </div>
        )}
        <DrawerFieldRenderer
          fields={schema.fields}
          formData={formData}
          fieldErrors={fieldErrors}
          mode={mode}
          onChange={handleFieldChange}
          onToggleChange={handleToggleChange}
          getDeepValue={getDeepValue}
        />
      </div>

      <div className="config-drawer-actions">
        {isEdit ? (
          <>
            <Button size="medium" variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              size="medium"
              variant="primary"
              disabled={hasFieldErrors || !isUpdated}
              onClick={() => setConfirmationModalOpen(true)}
            >
              Save
            </Button>
          </>
        ) : (
          <Button size="medium" variant="secondary" onClick={onClose}>
            Close
          </Button>
        )}
      </div>

      <ConfigurationModals
        confirmationModalOpen={confirmationModalOpen}
        discardModalOpen={discardModalOpen}
        setConfirmationModalOpen={setConfirmationModalOpen}
        setDiscardModalOpen={() => setDiscardModalOpen(false)}
        isLoading={isLoading}
        handleSaveConfirmation={handleSaveConfirmation}
        navigateToSystemSettings={handleDiscardAndClose}
        configType={schema.title.toLowerCase()}
      />

      <ToastNotification
        {...toast}
        handleClose={() => {
          setToast({ ...toast, open: false });
        }}
      />
    </>
  );
};

FieldBasedConfigDrawer.propTypes = {
  configKey: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["edit", "view"]).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

// ─── JsonForms-Based Drawer ─────────────────────────────────────────────────

const JsonFormsConfigDrawer = ({
  configKey,
  mode,
  isOpen,
  onClose,
  onEdit,
  canEdit,
}) => {
  const config = jsonFormsConfigs[configKey];

  return (
    <>
      <DrawerHeader
        description={config.description}
        onClose={onClose}
        showClose={mode === "view"}
        title={config.title}
      />

      <JsonFormsDrawerContent
        configKey={configKey}
        mode={mode}
        isOpen={isOpen}
        onClose={onClose}
        onEdit={onEdit}
        canEdit={canEdit}
        schemaSlug={config.schemaSlug}
      />
    </>
  );
};

JsonFormsConfigDrawer.propTypes = {
  configKey: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["edit", "view"]).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  canEdit: PropTypes.bool,
};

// ─── Dynamic Field-Based Drawer (auto-generated from data) ─────────────────

const DynamicFieldConfigDrawer = ({ configKey, mode, isOpen, onClose }) => {
  const { programConfigurations } = useContext(GlobalConfigContext);
  const isEdit = mode === "edit";

  const configEntry = useMemo(
    () => programConfigurations?.find((c) => c.key === configKey),
    [programConfigurations, configKey]
  );

  const dynamicSchema = useMemo(() => {
    const title =
      configEntry?.value?.title ||
      configKey.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const description = configEntry?.value?.description || "";
    const fields = generateFieldsFromData(configEntry?.value?.data || {});
    return { title, description, fields };
  }, [configEntry, configKey]);

  const {
    formData,
    fieldErrors,
    isUpdated,
    isLoading,
    hasFieldErrors,
    confirmationModalOpen,
    discardModalOpen,
    toast,
    setToast,
    setConfirmationModalOpen,
    setDiscardModalOpen,
    handleFieldChange,
    handleToggleChange,
    handleSaveConfirmation,
    handleDiscard,
    getDeepValue,
  } = useDrawerConfigForm(configKey, {}, isOpen);

  const handleClose = () => {
    if (isEdit && isUpdated) {
      setDiscardModalOpen(true);
    } else {
      onClose();
    }
  };

  const handleDiscardAndClose = () => {
    handleDiscard();
    onClose();
  };

  return (
    <>
      <div className="config-drawer-header">
        <p className="config-drawer-title">{dynamicSchema.title}</p>
        {dynamicSchema.description && (
          <p className="config-drawer-description">{dynamicSchema.description}</p>
        )}
      </div>

      <div className="config-drawer-content">
        {dynamicSchema.fields.length > 0 ? (
          <DrawerFieldRenderer
            fields={dynamicSchema.fields}
            formData={formData}
            fieldErrors={fieldErrors}
            mode={mode}
            onChange={handleFieldChange}
            onToggleChange={handleToggleChange}
            getDeepValue={getDeepValue}
          />
        ) : (
          <p className="config-drawer-empty">No configuration fields available.</p>
        )}
      </div>

      <div className="config-drawer-actions">
        {isEdit ? (
          <>
            <Button size="medium" variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              size="medium"
              variant="primary"
              disabled={hasFieldErrors || !isUpdated}
              onClick={() => setConfirmationModalOpen(true)}
            >
              Save
            </Button>
          </>
        ) : (
          <Button size="medium" variant="secondary" onClick={onClose}>
            Close
          </Button>
        )}
      </div>

      <ConfigurationModals
        confirmationModalOpen={confirmationModalOpen}
        discardModalOpen={discardModalOpen}
        setConfirmationModalOpen={setConfirmationModalOpen}
        setDiscardModalOpen={() => setDiscardModalOpen(false)}
        isLoading={isLoading}
        handleSaveConfirmation={handleSaveConfirmation}
        navigateToSystemSettings={handleDiscardAndClose}
        configType={dynamicSchema.title.toLowerCase()}
      />

      <ToastNotification
        {...toast}
        handleClose={() => {
          setToast({ ...toast, open: false });
        }}
      />
    </>
  );
};

DynamicFieldConfigDrawer.propTypes = {
  configKey: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["edit", "view"]).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
